import React, { useMemo } from 'react'
import log from 'loglevel'
import { Flags, FlagsProvider } from 'react-feature-flags'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  FeatureAccessRequest,
  FeatureAccessResponse,
  FEATURE_ACCESS,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useUser } from 'src/components/async/Users'
import { usePlaybooks } from 'src/pages/TrusteroIntelligence/playbooks/playbooks.hooks'
import { useTrustPortalAccountId } from 'src/pages/TrustContentManagement/trustPortalConfiguration.hooks'
import { loadFromStorage } from '../Utils/storage'
import { EnvConfig } from '../config/config'
import {
  useIsAdmin,
  useIsInternalUser,
  useIsProvider,
  useIsTrustPortalApprover,
  useIsTrustPortalPublisher,
} from './authContext'

const logger = log.getLogger('context')

export enum GatekeeperFlag {
  REGISTER_PAGES = 'registerPages',
  RELEVANT_DATE = 'relevantDate',
  MULTI_FRAMEWORK = 'multiFramework',
  CUSTOM_RECEPTOR = 'customReceptor',
  GRID_COLUMN_SORT = 'gridColumnSort',
  SUGGESTED_AUTOMATED_EVIDENCE = 'suggestedAutomatedEvidence',
  MULTI_FRAMEWORK_SUGGESTIONS = 'multiFrameworkSuggestions',
  STUB_FRAMEWORKS = 'stubFrameworks',
  REPORT_SCAN = 'reportScan',
  SUGGESTED_DLR_EVIDENCE = 'suggestedDLREvidence',
  CRITERIA_CONTROL_MAPPING = 'criteriaControlMapping',
  CONTROL_IDS = 'controlIds',
  RISK_REGISTER = 'riskRegister',
  STANDALONE_SCANNER = 'standaloneScanner',
  NAV_UPDATES = 'navUpdates',
  SNYK_RECEPTOR = 'snykReceptor',
  AUDIT_BOT = 'auditBot',
  SERVICES_OVERHAUL = 'servicesOverhaul',
  MIRADORE_RECEPTOR = 'miradoreReceptor',
  SOPHOS_RECEPTOR = 'sophosReceptor',
  VENDOR_MANAGEMENT = 'vendorManagement',
  JUMPCLOUD_RECEPTOR = 'jumpcloudReceptor',
  MS_LOGIN = 'msLogin',
  KNOWBE4_RECEPTOR = 'knowbe4Receptor',
  MULTI_FRAMEWORK_AUDITBOT = 'multiFrameworkAuditbot',
  SERVICE_ACCOUNTS = 'serviceAccounts',
  ASK_AI_COPILOT = 'askAiCopilot',
  CC_ROADMAP = 'ccRoadmap',
  CONTINUOUS_MONITORING = 'continuousMonitoring',
  GOOGLE_DRIVE_AUTH = 'googleDriveAuth',
  JIRA_RECEPTOR = 'jiraReceptor',
  GOOGLE_DRIVE_POLICY_IMPORT = 'googleDrivePolicyImport',
  POLICY_RESPONSIBILITY = 'policyResponsibility',
  NEW_EVIDENCE_TABLE = 'newEvidenceTable',
  CONTROL_EVIDENCE_NEW_TAB = 'controlEvidenceNewTab',
  GENERATE_SYSTEM_DESCRIPTION = 'generateSystemDescription',
  ANECDOTES_RECEPTOR = 'anecdotesReceptor',
  RELEVANT_DATE_FORM = 'relevantDateForm',
  EVIDENCE_INDEX = 'evidenceIndex',
  SLACK_RECEPTOR = 'slackReceptor',
  ROADMAP_UPDATES = 'roadmapUpdates',
  SONARQUBE_CLOUD_RECEPTOR = 'sonarqubeCloudReceptor',
  FRESHDESK_RECEPTOR = 'freshdeskReceptor',
  JSM_RECEPTOR = 'jsmReceptor',
  CONFLUENCE_RECEPTOR = 'confluenceReceptor',
  ZENDESK_RECEPTOR = 'zendeskReceptor',
  TRUSTERO_INTELLIGENCE_V1 = 'trusteroIntelligenceV1',
  PINGONE_LOGIN = 'pingOneLogin',
  CITE_SOURCES = 'citeSources',
  HUBSPOT_RECEPTOR = 'hubspotReceptor',
  OKTA_RECEPTOR = 'oktaReceptor',
  TEST_TABLE = 'testTable',
  AZUREBOARDS_RECEPTOR = 'azureBoardsReceptor',
  USE_AI_QUESTION_USAGE_TYPE = 'useAiQuestionUsageType',
  FLAT_HELP_PAGE = 'flatHelpPage',
  RIPPLING_RECEPTOR = 'ripplingReceptor',
  TRUST_PORTAL = 'trustPortal',
  USE_INDEXING_STATUS_PAGE = 'useIndexingStatusPage',
  ARCHER_RECEPTOR = 'archerReceptor',
  JIT_RECEPTOR = 'jitReceptor',
  SHAREPOINT = 'sharepoint',
  SHOW_CHAT_STATUS = 'showChatStatus',
  LEVEL_OF_DETAIL = 'levelOfDetail',
  RISK_CATEGORY_CONFIGURATION = 'riskCategoryConfiguration',
  JIRA_WORKFLOW = 'jiraWorkflow',
  AGENT_CHAT = 'agentChat',
  CHAT_SEARCH = 'chatSearch',
  SERVICENOW_WORKFLOW = 'servicenowWorkflow',
  GRC_DASHBOARDS = 'grcDashboards',
  NEW_TRUST_PORTAL = 'newTrustPortal',
  RISK_RESPONSE_DASHBOARD = 'riskResponseDashboard',
  DOCEBO_RECEPTOR = 'doceboReceptor',
  PLAYBOOK = 'playbook',
  SALESFORCE_RECEPTOR = 'salesforceReceptor',
  CONFLUENCE_DMS = 'confluenceDMS',
  VIEW_LINKER_DOCUMENTS = 'viewLinkerDocuments',
  USE_SCHEDULING = 'useScheduling',
  PLAYBOOKS_UI = 'playbooksUi',
  CONTROL_EVIDENCE_MAPPINGS = 'controlEvidenceMappings',
  NEW_EVIDENCE_PAGE = 'newEvidencePage',
  APP_COPILOT = 'appCopilot',
  COPILOT_SHOW_PAGE = 'copilotShowPage',
  MULTIPLE_TEST_PROCEDURES = 'multipleTestProcedures',
  MULTI_POLICY_CONTROL_LINKAGES = 'multiPolicyControlLinkages',
  NEW_LOGIN_PAGE = 'newLoginPage',
  NEW_POLICY_PAGE = 'newPolicyPage',
  POLICY_FOLDER_SCAN = 'policyFolderScan',
  CUSTOM_CONTROL_STATUSES = 'customControlStatuses',
  CONTROL_STATUS_COLOR_PICKER = 'controlStatusColorPicker',
  SHOW_EDITED_TEST_DETAILS = 'showEditedTestDetails',
  INCIDENTIO = 'incidentioReceptor',
  CROWDSTRIKE_RECEPTOR = 'crowdstrikeReceptor',
  NEW_REQUESTS = 'newRequests',
  LABELS = 'labels',
  OVERVIEW_DASHBOARD = 'overviewDashboard',
  OVERVIEW_DASHBOARD_DRAGGABLE = 'overviewDashboardDraggable',
  METRICSTREAM_RECEPTOR = 'metricstreamReceptor',
}

function isLocallyEnabled(flag: string): boolean | null {
  // for our internal use, we want to allow users to override the value
  // of feature flags by setting keys in localStorage
  // Keys will be named "<env>_feature_<flag>"
  // The default value of `null` allows us to tell the caller
  // that the value was not found in localStorage
  const value = loadFromStorage(`feature_${flag}`, null)
  return value
}

export const GatekeeperProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  // verify that all defined flags are in our enum
  const flagData =
    (EnvConfig.featureFlags as { [key in GatekeeperFlag]: boolean }) || {}
  const allowedFlags = Object.values(GatekeeperFlag)
  for (const flag of Object.keys(flagData) as GatekeeperFlag[]) {
    if (!allowedFlags.includes(flag)) {
      logger.error(`Unknown gatekeeper flag in config file: ${flag}`)
    }
  }
  // delegate to FlagsProvider
  const featureFlags = Object.entries(EnvConfig.featureFlags || {}).map(
    ([flag, isEnvEnabled]) => {
      return {
        name: flag,
        isActive: isLocallyEnabled(flag) ?? isEnvEnabled,
      }
    },
  )
  return <FlagsProvider value={featureFlags}>{children}</FlagsProvider>
}

/*
 * This function is used to determine if a feature flag is enabled.
 * @param flag - The gatekeeper flag(s) to check - can be a single flag or an array of flags
 * @returns true if all of the flags are enabled, false otherwise
 */

export const IsGatekeeperEnabled = (
  flag: GatekeeperFlag | GatekeeperFlag[],
): boolean => {
  // We only want to return true if all flags are enabled
  if (Array.isArray(flag)) {
    return !flag.some((f) => !IsGatekeeperEnabled(f))
  }

  const isEnvEnabled = EnvConfig?.featureFlags?.[flag] === true

  return isLocallyEnabled(flag) ?? isEnvEnabled
}

type GatekeeperProps = {
  authorizedFlags: GatekeeperFlag[]
  children?: JSX.Element | JSX.Element[]
  renderOff?: () => JSX.Element | null
  renderOn?: () => JSX.Element | null
}

export const Gatekeeper = ({
  authorizedFlags,
  renderOff = () => null,
  renderOn = () => null,
  children,
}: GatekeeperProps): JSX.Element => {
  if (authorizedFlags.some((f) => !Object.values(GatekeeperFlag).includes(f))) {
    logger.error('Invalid Gatekeeper flag was passed in')
    return <></>
  }

  return (
    <Flags
      renderOff={renderOff}
      renderOn={renderOn}
      authorizedFlags={authorizedFlags}
    >
      {children}
    </Flags>
  )
}

type ReverseGatekeeperComponentProps = {
  shouldHide: <t>(arg: t) => boolean
  shouldHideArg: unknown
  authorizedFlags: GatekeeperFlag | GatekeeperFlag[]
  children?: JSX.Element | JSX.Element[]
}

/**
 * This component is used to hide a component if the gatekeeper flag is NOT enabled and the shouldHide function returns true
 * @param shouldHide - a function that takes in a generic argument and returns a boolean
 * @param shouldHideArg - the argument to pass to the shouldHide function
 * @param authorizedFlags - the gatekeeper flag(s) to check
 * @param children - the component to render if the gatekeeper flag is enabled or the shouldHide function returns false
 * @returns the null if the gatekeeper flag is NOT enabled and the shouldHide function returns true, otherwise it returns the children
 */
export const ReverseGatekeeperComponent = ({
  shouldHide,
  shouldHideArg,
  authorizedFlags,
  children,
}: ReverseGatekeeperComponentProps): JSX.Element => {
  const gatekeeper = IsGatekeeperEnabled(authorizedFlags)
  return !gatekeeper && shouldHide(shouldHideArg) ? <></> : <>{children}</>
}

/**
 * Determine feature access from the backend
 */
export const useHasFeatureAccess = (
  featureType: FEATURE_ACCESS,
  shouldFetch = true,
): GrpcResponse<FeatureAccessResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.checkFeatureAccess,
    new FeatureAccessRequest().setFeatureType(featureType),
    shouldFetch && featureType > -1, // Ensure we don't call the API if the feature type is not valid (begins with 0 enum)
  )

  return NewGrpcResponse(response)
}

export const useHasGoogleDriveAuth = (): boolean => {
  const hasEnvAccess = IsGatekeeperEnabled(GatekeeperFlag.GOOGLE_DRIVE_AUTH)

  return hasEnvAccess
}

export const hasControlStatus = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.CONTINUOUS_MONITORING)

export const useHasGenerateSystemDescription = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.GENERATE_SYSTEM_DESCRIPTION)

export const useHasAnecdotesReceptor =
  (): GrpcResponse<FeatureAccessResponse> => {
    return useHasFeatureAccess(FEATURE_ACCESS.ANECDOTES_EVIDENCE_INDEX)
  }

export const useHasAnecdotesReceptorAccess = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.ANECDOTES_RECEPTOR)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasArcherReceptorAccess = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.ARCHER_RECEPTOR)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasMetricStreamReceptor = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.METRICSTREAM_RECEPTOR)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasNotificationInbox = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.NOTIFICATION_INBOX)
  return useMemo(() => data?.getAllowAccess() ?? false, [data])
}

export const useHasTrusteroIntelligence = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.TRUSTERO_INTELLIGENCE)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasRiskCategoryConfiguration = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.RISK_CATEGORY_CONFIGURATION)
}

export const useHasTrustPortalConfiguration = (): boolean => {
  const isProvider = useIsProvider()
  const isAdmin = useIsAdmin()
  const isTrustPortalPublisher = useIsTrustPortalPublisher()
  const isTrustPortalApprover = useIsTrustPortalApprover()
  const trustPortalAccountId = useTrustPortalAccountId()
  return (
    isProvider &&
    (isAdmin || isTrustPortalPublisher || isTrustPortalApprover) &&
    !!trustPortalAccountId
  )
}

export const hasRoadmapUpdates = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.ROADMAP_UPDATES)

export const useFlatHelpPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.FLAT_HELP_PAGE)

export const useNewRequests = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.NEW_REQUESTS)

export const useIndexingStatusPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.USE_INDEXING_STATUS_PAGE)

export const useSharepointLinkPaste = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.USE_SHAREPOINT_LINK_PASTE)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useRipplingReceptor = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.RIPPLING_RECEPTOR)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useConfigLLMModel = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.LLM_MODEL_CONFIG)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasGRCContentCleanup = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.GRC_CONTENT_CLEANUP)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasContentPublishing = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.CONTENT_PUBLISHING)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useIsGlobalContentAccount = (): boolean => {
  const { data: currentUser } = useUser()
  const { data: playbooks } = usePlaybooks({ includeGlobalPlaybooks: true })

  return useMemo(() => {
    if (!currentUser || !playbooks) return false
    const currentAccountId = currentUser.getAccountid()
    return playbooks
      .getPlaybooksList()
      .some((p) => p.getIsGlobal() && p.getAccountId() === currentAccountId)
  }, [currentUser, playbooks])
}

export const useAgentChat = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.AGENT_CHAT)
  return (
    data?.getAllowAccess() || IsGatekeeperEnabled(GatekeeperFlag.AGENT_CHAT)
  )
}

export const useHasGRCDashboards = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.GRC_DASHBOARDS)
  const { data: user } = useUser()
  const { data: featureAccess } = useHasFeatureAccess(
    FEATURE_ACCESS.GRC_DASHBOARDS,
  )
  const isTrustedUser = useMemo(
    () => user?.getEmail()?.toLowerCase().endsWith('@trustero.com') || false,
    [user],
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || isTrustedUser || hasDashboards
}

export const useHasRiskDashboard = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.RISK_RESPONSE_DASHBOARD,
  )
  const { data: featureAccess } = useHasFeatureAccess(
    FEATURE_ACCESS.RISK_DASHBOARD,
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || hasDashboards
}

export const useOverviewDashboardAccess = (): {
  hasAccess: boolean
  loading: boolean
} => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.OVERVIEW_DASHBOARD)
  const isInternalUser = useIsInternalUser()
  const { data: featureAccess, loading } = useHasFeatureAccess(
    FEATURE_ACCESS.OVERVIEW_DASHBOARD,
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  const hasAccess = isFlagEnabled || isInternalUser || hasDashboards
  return {
    hasAccess,
    loading: !hasAccess && loading,
  }
}

export const useHasOverviewDashboard = (): boolean => {
  const { hasAccess } = useOverviewDashboardAccess()
  return hasAccess
}

export const useHasOverviewDashboardDraggable = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.OVERVIEW_DASHBOARD_DRAGGABLE)

export const useHasPlaybook = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.PLAYBOOK)
}

export const useHasScheduling = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.USE_SCHEDULING)
  const { data: featureAccess } = useHasFeatureAccess(FEATURE_ACCESS.SCHEDULING)
  const hasAccess = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || hasAccess
}

export const useHasPlaybooksUi = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.PLAYBOOKS_UI)
}

export const useHasNewEvidencePage = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_EVIDENCE_PAGE)
}

export const useHasNewPolicyPage = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_POLICY_PAGE)
}

export const useHasAppCopilot = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.APP_COPILOT)
  return (
    data?.getAllowAccess() || IsGatekeeperEnabled(GatekeeperFlag.APP_COPILOT)
  )
}

export const useHasCopilotShowPage = (): boolean => {
  const isInternalUser = useIsInternalUser()
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.COPILOT_SHOW_PAGE)
  return (
    data?.getAllowAccess() ||
    IsGatekeeperEnabled(GatekeeperFlag.COPILOT_SHOW_PAGE) ||
    isInternalUser
  )
}

export const useHasGenerateControls = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.GENERATE_CONTROLS)
  return data?.getAllowAccess() || false
}

export const useHasBigMenuNav = (): {
  hasAccess: boolean
  loading: boolean
} => {
  return {
    hasAccess: true,
    loading: false,
  }
}

export const useHasCustomControlStatuses = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CUSTOM_CONTROL_STATUSES)
}

export const useHasControlStatusColorPicker = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CONTROL_STATUS_COLOR_PICKER)
}
